import { Box, Button, Container, Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { InformationModal } from 'utoniq-ui'

import { BaseLayout, Head } from '@/components/layouts'
import { Loading } from '@/components/organisms/loading'
import { StorageImage, useMessage, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { generateCreditCardLogoPath } from '@/core/creditCard'

export * from './fragments'

export type State = {
  cardNumber?: string
  name?: string
  expirationDate: string
  visible: boolean
  brand?: string
}

export type Action = {
  onDeletePaymentSource: () => Promise<void>
}

export const PaymentDetailTemplate: Template<State, Action> = ({
  state,
  onDeletePaymentSource,
}) => {
  const { t } = useTranslation('paymentDetail')
  const { router, routerNames, routerPages } =
    useRouter<{ reRegister?: string }>()
  const { showMessage } = useMessage()

  // HACK: PointChargeDrawerから遷移した時、モーダルを非表示にする
  useEffect(() => {
    const elms = document?.getElementsByClassName('show-modal')
    Array.from(elms).forEach(elm => elm.setAttribute('is-open', 'false'))
  }, [])

  /**
   * @description
   * 決済エラー時に表示されるカードの再登録を促すモーダルから
   * 遷移してきた場合にトーストメッセージを表示する
   */
  useEffect(() => {
    if (router.query.reRegister) {
      showMessage(t('reRegisterInfo'), 'info')
    }
    /**
     * 依存配列に showMessage を渡すと無限ループが発生するため空配列を渡し、
     *  eslint を無効化して Warning を消している
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <Head title={routerNames.PaymentDetail} />
      <BaseLayout
        header={{
          title: t('title'),
          back: true,
          defaultBackPage: routerPages.Setting,
        }}
      >
        <CreditCardCell
          cardNumber={state.cardNumber}
          name={state.name}
          expirationDate={state.expirationDate}
          visible={state.visible}
          onDeletePaymentSource={onDeletePaymentSource}
          brand={state.brand}
        />
      </BaseLayout>
    </>
  )
}

type Props = {
  cardNumber?: string
  name?: string
  expirationDate: string
  visible: boolean
  onDeletePaymentSource: Action['onDeletePaymentSource']
  brand?: string
}

export const CreditCardCell: Cell<Props> = ({
  cardNumber,
  name,
  expirationDate,
  visible,
  onDeletePaymentSource,
  brand,
}) => {
  const { t } = useTranslation('paymentDetail')
  const { routerPages } = useRouter()
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const { showMessage, showError } = useMessage()

  const handleDeleteCreditCard = () => {
    setLoading(true)
    onDeletePaymentSource()
      .then(() => showMessage(t('deleteSuccess')))
      .catch((e: Error) => showError(e))
      .finally(() => {
        setLoading(false)
        setOpen(false)
      })
  }

  if (cardNumber && !visible) {
    return (
      <CellFrame sx={{ px: 5, pt: 2 }}>
        <Stack
          direction="row"
          sx={{
            py: 6,
            px: 5,
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 2,
          }}
          spacing={5}
          justifyContent="flex-start"
          alignItems="flex-start"
        >
          <StorageImage
            width={40}
            height={32}
            src={generateCreditCardLogoPath(brand)}
            target="common"
          />
          <Stack flex={1} spacing={1}>
            <Typography variant="h6">
              {`**** **** **** ${cardNumber}`}
            </Typography>
            <Typography variant="caption">{name}</Typography>
            <Typography variant="caption">
              {t('dateOfExpiry')}: {expirationDate}
            </Typography>
          </Stack>
          <Button
            variant="text"
            onClick={() => setOpen(true)}
            size="small"
            sx={{ minWidth: 'auto', p: 0 }}
          >
            <Typography variant="subtitle1" color="primary.main">
              {t('delete')}
            </Typography>
          </Button>
        </Stack>
        <InformationModal
          open={open}
          onClose={() => setOpen(false)}
          title={t('deleteTitle')}
          content={t('deleteConfirm')}
          cancelText={t('cancel')}
          buttons={
            <Button
              onClick={handleDeleteCreditCard}
              size="large"
              sx={{ minWidth: 212 }}
              loading={loading}
            >
              OK
            </Button>
          }
        />
      </CellFrame>
    )
  }

  return (
    <Stack flex={1} sx={{ height: '100%' }}>
      {visible && (
        <Container
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Loading />
        </Container>
      )}
      {!cardNumber && !visible && (
        <Stack
          sx={{ height: '100%' }}
          justifyContent="center"
          alignItems="center"
        >
          <Box>
            <Typography textAlign="center" sx={{ py: 4 }}>
              {t('noPaymentMethod')}
            </Typography>
            <Button
              size="large"
              href={routerPages.PaymentMethodSetting}
              sx={{ width: 244, margin: 'auto' }}
              color="primary"
            >
              {t('registerCredit')}
            </Button>
          </Box>
        </Stack>
      )}
    </Stack>
  )
}
